import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogActions, DialogTitle, LinearProgress, IconButton } from '@mui/material';
import voucherOutItemServices from '../voucherOutItemServices';
import { useAlert } from '../../../../../../context/AlertContext';
import { Edit } from '@mui/icons-material';

function VoucherItemDescription({ voucherItem, isPrinting,getItemList }) {
    const { AppAlert } = useAlert();

    const [inputTitle, setInputTitle] = useState(voucherItem?.title || "");
    const [inputDetails, setInputDetails] = useState(voucherItem?.details || "");
    const [inputHsn, setInputHsn] = useState(voucherItem?.hsn || "");

    const [dialogStatus, setDialogStatus] = useState(false);
    const [saving, setSavingStatus] = useState(false);

    // Sync state when voucherItem prop changes or description exists
    useEffect(() => {
        if (voucherItem?.description) {
            try {
                const parsedDescription = typeof voucherItem.description === 'string' 
                    ? JSON.parse(voucherItem.description) 
                    : voucherItem.description;

                setInputTitle(parsedDescription?.title ?? voucherItem?.title ?? "");
                // FIX: Saved key is "details", not "inputDetails"
                setInputDetails(parsedDescription?.details ?? voucherItem?.details ?? "");
                setInputHsn(parsedDescription?.hsn ?? voucherItem?.hsn ?? "");
            } catch (e) {
                // Fallback if JSON parsing fails
                setInputTitle(voucherItem?.title || "");
                setInputDetails(voucherItem?.details || "");
                setInputHsn(voucherItem?.hsn || "");
            }
        } else {
            setInputTitle(voucherItem?.title || "");
            setInputDetails(voucherItem?.details || "");
            setInputHsn(voucherItem?.hsn || "");
        }
    }, [voucherItem]);

    // Revert form state back to current saved state on cancel/close
    const handleResetInputs = () => {
        if (voucherItem?.description) {
            try {
                const parsed = typeof voucherItem.description === 'string'
                    ? JSON.parse(voucherItem.description)
                    : voucherItem.description;
                setInputTitle(parsed?.title ?? voucherItem?.title ?? "");
                setInputDetails(parsed?.details ?? voucherItem?.details ?? "");
                setInputHsn(parsed?.hsn ?? voucherItem?.hsn ?? "");
                return;
            } catch (e) {}
        }
        setInputTitle(voucherItem?.title || "");
        setInputDetails(voucherItem?.details || "");
        setInputHsn(voucherItem?.hsn || "");
    };

    const handleUpdate = () => {
        setSavingStatus(true);

        const descriptionPayload = {
            title: inputTitle,
            details: inputDetails,
            hsn:inputHsn
        };

        const payload = { 
            voucherItemId: voucherItem.id,
            description: JSON.stringify(descriptionPayload)
        };

        voucherOutItemServices.updateDescription(payload)
            .then(res => {
                AppAlert(res?.msg || "Description updated successfully");
                setDialogStatus(false); // Close dialog on success
                getItemList();
            })
            .catch(error => {
                AppAlert(error?.msg || "Failed to update description", "error");
            })
            .finally(() => {
                setSavingStatus(false);
            });
    };

    const handleClose = () => {
        if (saving) return; // Prevent closing while saving
        handleResetInputs();
        setDialogStatus(false);
    };

    return (
        <>
            <div className='d-flex'>
                <div>
                    <div className="fw-bold">{inputTitle}</div>
                    <div className="text-muted" style={{ whiteSpace: 'pre-line' }}>{inputDetails}</div>
                </div>
                <div>
                    {!isPrinting && (
                        <IconButton 
                            type="button" 
                            className='btn btn-sm btn-outline-primary mt-1' 
                            style={{padding:"0px"}} className="delete-icon ms-2"
                            onClick={() => setDialogStatus(true)}
                        >
                            <Edit/>
                        </IconButton>
                    )}
                </div>
            </div>

            <Dialog
                open={dialogStatus}
                keepMounted
                onClose={handleClose}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Update Description</DialogTitle>
                
                {saving && <LinearProgress />}

                <DialogContent>
                    {/* Title Section */}
                    <div className='row mb-3 align-items-center'>
                        <div className='col-md-8 fw-bold'>Title</div>
                        <div className='col-md-4'>
                            <button 
                                type="button" 
                                className='float-end btn btn-sm btn-outline-secondary' 
                                onClick={() => setInputTitle(voucherItem?.title || "")}
                                disabled={saving}
                            >
                                Use Default Title
                            </button>
                        </div>
                        <div className='col-md-12 mt-2'>
                            <input 
                                type='text' 
                                className='form-control'  
                                value={inputTitle} 
                                onChange={(e) => setInputTitle(e.target.value)} 
                                disabled={saving}
                            />
                        </div>
                    </div>

                    {/* Details Section */}
                    <div className='row align-items-center'>
                        <div className='col-md-8 fw-bold'>Details</div>
                        <div className='col-md-4'>
                            <button 
                                type="button" 
                                className='float-end btn btn-sm btn-outline-secondary' 
                                // FIX: Updated setter to setInputDetails instead of setInputTitle
                                onClick={() => setInputDetails(voucherItem?.details || "")}
                                disabled={saving}
                            >
                                Use Default Details
                            </button>
                        </div>
                        <div className='col-md-12 mt-2'>
                            <textarea 
                                className='form-control' 
                                rows={4} 
                                value={inputDetails} 
                                onChange={(e) => setInputDetails(e.target.value)} 
                                disabled={saving}
                            />
                        </div>
                    </div>

                    <div className='row mb-3 align-items-center'>
                        <div className='col-md-8 fw-bold'>HSN</div>
                        <div className='col-md-4'>
                            <button 
                                type="button" 
                                className='float-end btn btn-sm btn-outline-secondary' 
                                onClick={() => setInputHsn(voucherItem?.hsn || "")}
                                disabled={saving}
                            >
                                Use Default Hsn
                            </button>
                        </div>
                        <div className='col-md-12 mt-2'>
                            <input 
                                type='text' 
                                className='form-control'  
                                value={inputHsn} 
                                onChange={(e) => setInputHsn(e.target.value)} 
                                disabled={saving}
                            />
                        </div>
                    </div>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <button 
                        type="button" 
                        className='btn btn-outline-secondary' 
                        onClick={handleClose}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button 
                        type="button" 
                        className='btn btn-primary' 
                        onClick={handleUpdate}
                        disabled={saving}
                    >
                        {saving ? "Updating..." : "Update"}
                    </button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default VoucherItemDescription;